import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { NAV_LINKS, LOGO_DARK } from "../data/site";
import { useSite } from "../context/ContentContext";
import { subscribeNewsletter } from "../lib/api";

export function Footer() {
  const { company: COMPANY, services: SERVICES } = useSite();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Thanks for subscribing!");
      setEmail("");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-[#06281A] text-slate-300" data-testid="main-footer">
      <div className="container-px mx-auto max-w-7xl py-16">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="inline-flex items-center justify-center rounded-2xl bg-white/95 p-3 shadow-lg">
              <img src={LOGO_DARK} alt="Domaleo Enterprise" className="h-20 w-auto object-contain" />
            </div>
            <p className="mt-5 text-sm leading-relaxed text-slate-400">{COMPANY.tagline}</p>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              Reliable import & export, IT, agriculture, logistics and general supply solutions across Ghana and beyond.
            </p>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white">Quick Links</h4>
            <ul className="mt-4 space-y-3 text-sm">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="text-slate-400 transition-colors hover:text-accent" data-testid={`footer-link-${l.label.toLowerCase()}`}>
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white">Our Services</h4>
            <ul className="mt-4 space-y-3 text-sm">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-slate-400 transition-colors hover:text-accent">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-white">Stay Connected</h4>
            <ul className="mt-4 space-y-3 text-sm text-slate-400">
              <li className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-accent" /> {COMPANY.address}</li>
              <li className="flex flex-col gap-1">
                {((COMPANY.phones && COMPANY.phones.length) ? COMPANY.phones : [COMPANY.phone]).map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-accent transition-colors"><Phone className="h-4 w-4 text-accent" /> {p}</a>
                ))}
              </li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-accent" /> {COMPANY.email}</li>
            </ul>
            <form onSubmit={onSubscribe} className="mt-5" data-testid="newsletter-form">
              <label className="text-xs uppercase tracking-wider text-slate-500">Newsletter</label>
              <div className="mt-2 flex">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="w-full rounded-l-full bg-white/5 border border-white/10 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-accent"
                  data-testid="newsletter-email-input"
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center rounded-r-full bg-accent px-4 text-[#06281A] hover:bg-accent/90 disabled:opacity-60"
                  data-testid="newsletter-submit-btn"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </button>
              </div>
            </form>
            <div className="mt-5 flex gap-3">
              {COMPANY.socials.map((s) => (
                <a key={s.name} href={s.url} className="text-xs text-slate-400 hover:text-accent transition-colors">{s.name}</a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-white/10 pt-6 text-sm text-slate-500 md:flex-row md:items-center md:justify-between">
          <p>© {new Date().getFullYear()} Domaleo Enterprise. All rights reserved.</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <Link to="/privacy" className="hover:text-accent transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-accent transition-colors">Terms of Service</Link>
            <Link to="/cookies" className="hover:text-accent transition-colors">Cookie Policy</Link>
            <a href="https://domaleogh.com" target="_blank" rel="noopener noreferrer" className="hover:text-accent transition-colors" data-testid="footer-credit">
              Created by <span className="font-semibold text-slate-300">Domaleo IT Solutions</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
